import ApiClient from "../helpers/ApiClient";
import useSWR from "swr";

const fetcher = (url) => ApiClient.get(url).then((res) => res.data);

// ─────────────────────────────────────────────────────────────
// SWR HOOKS
// ─────────────────────────────────────────────────────────────

export function useAssets(filters = {}) {
  const queryParams = new URLSearchParams(filters).toString();
  const APIURL = `/assets${queryParams ? `?${queryParams}` : ''}`;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);
  return { data, error, isLoading, mutate, APIURL };
}

export function useAssetDetails(id) {
  const APIURL = id ? `/assets/${id}` : null;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);
  return { data, error, isLoading, mutate, APIURL };
}

export function useAssetCategories(filters = {}) {
  const queryParams = new URLSearchParams(filters).toString();
  const APIURL = `/assets/categories${queryParams ? `?${queryParams}` : ''}`;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);
  return { data, error, isLoading, mutate, APIURL };
}

export function useAssetDashboard() {
  const APIURL = `/assets/dashboard`;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);
  return { data, error, isLoading, mutate, APIURL };
}

export function useAssetAcquisitions(filters = {}) {
  const queryParams = new URLSearchParams(filters).toString();
  const APIURL = `/assets/acquisitions${queryParams ? `?${queryParams}` : ''}`;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);
  return { data, error, isLoading, mutate, APIURL };
}

export function useAssetTransfers(filters = {}) {
  const queryParams = new URLSearchParams(filters).toString();
  const APIURL = `/assets/transfers${queryParams ? `?${queryParams}` : ''}`;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);
  return { data, error, isLoading, mutate, APIURL };
}

export function useDamageReports(filters = {}) {
  const queryParams = new URLSearchParams(filters).toString();
  const APIURL = `/assets/damage-reports${queryParams ? `?${queryParams}` : ''}`;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);
  return { data, error, isLoading, mutate, APIURL };
}

export function useMaintenanceWorkOrders(filters = {}) {
  const queryParams = new URLSearchParams(filters).toString();
  const APIURL = `/assets/maintenance${queryParams ? `?${queryParams}` : ''}`;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);
  return { data, error, isLoading, mutate, APIURL };
}

export function usePreventiveSchedules(filters = {}) {
  const queryParams = new URLSearchParams(filters).toString();
  const APIURL = `/assets/preventive-schedules${queryParams ? `?${queryParams}` : ''}`;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);
  return { data, error, isLoading, mutate, APIURL };
}

export function useVerificationSessions(filters = {}) {
  const queryParams = new URLSearchParams(filters).toString();
  const APIURL = `/assets/verifications${queryParams ? `?${queryParams}` : ''}`;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);
  return { data, error, isLoading, mutate, APIURL };
}

export function useVerificationSessionDetails(id) {
  const APIURL = id ? `/assets/verifications/${id}` : null;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);
  return { data, error, isLoading, mutate, APIURL };
}

export function useDepreciationRuns() {
  const APIURL = `/assets/depreciation/runs`;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);
  return { data, error, isLoading, mutate, APIURL };
}

export function useDepreciationPreview(month, year) {
  const APIURL = (month && year) ? `/assets/depreciation/preview?month=${month}&year=${year}` : null;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);
  return { data, error, isLoading, mutate, APIURL };
}

export function useDisposals(filters = {}) {
  const queryParams = new URLSearchParams(filters).toString();
  const APIURL = `/assets/disposals${queryParams ? `?${queryParams}` : ''}`;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);
  return { data, error, isLoading, mutate, APIURL };
}

export function useAssetDocuments(assetId) {
  const APIURL = assetId ? `/assets/documents?asset_id=${assetId}` : `/assets/documents`;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);
  return { data, error, isLoading, mutate, APIURL };
}

export function useAssetReports(type = "asset_register", filters = {}) {
  const queryParams = new URLSearchParams({ type, ...filters }).toString();
  const APIURL = `/assets/reports?${queryParams}`;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);
  return { data, error, isLoading, mutate, APIURL };
}

export function useAssetSettings() {
  const APIURL = `/assets/settings`;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);
  return { data, error, isLoading, mutate, APIURL };
}

// ─────────────────────────────────────────────────────────────
// ASYNC API ACTIONS
// ─────────────────────────────────────────────────────────────

export async function createAsset(data) {
  const response = await ApiClient.post("/assets", data);
  return response.data;
}

export async function updateAsset(id, data) {
  const response = await ApiClient.put(`/assets/${id}`, data);
  return response.data;
}

export async function deleteAsset(id) {
  const response = await ApiClient.delete(`/assets/${id}`);
  return response.data;
}

export async function createAssetCategory(data) {
  const response = await ApiClient.post("/assets/categories", data);
  return response.data;
}

export async function updateAssetCategory(id, data) {
  const response = await ApiClient.put(`/assets/categories/${id}`, data);
  return response.data;
}

export async function deleteAssetCategory(id) {
  const response = await ApiClient.delete(`/assets/categories/${id}`);
  return response.data;
}

export async function createAcquisition(data) {
  const response = await ApiClient.post("/assets/acquisitions", data);
  return response.data;
}

export async function transferAsset(data) {
  const response = await ApiClient.post("/assets/transfers", data);
  return response.data;
}

export async function createDamageReport(data) {
  const response = await ApiClient.post("/assets/damage-reports", data);
  return response.data;
}

export async function updateDamageReport(id, data) {
  const response = await ApiClient.put(`/assets/damage-reports/${id}`, data);
  return response.data;
}

export async function recordMaintenance(data) {
  const response = await ApiClient.post("/assets/maintenance", data);
  return response.data;
}

export async function updateMaintenance(id, data) {
  const response = await ApiClient.put(`/assets/maintenance/${id}`, data);
  return response.data;
}

export async function createPreventiveSchedule(data) {
  const response = await ApiClient.post("/assets/preventive-schedules", data);
  return response.data;
}

export async function triggerPreventiveWorkOrder(scheduleId) {
  const response = await ApiClient.post(`/assets/preventive-schedules/${scheduleId}/trigger`);
  return response.data;
}

export async function createVerificationSession(data) {
  const response = await ApiClient.post("/assets/verifications", data);
  return response.data;
}

export async function recordVerificationScan(sessionId, data) {
  const response = await ApiClient.post(`/assets/verifications/${sessionId}/scan`, data);
  return response.data;
}

export async function completeVerificationSession(sessionId, data = {}) {
  const response = await ApiClient.post(`/assets/verifications/${sessionId}/complete`, data);
  return response.data;
}

export async function postDepreciationRun(data) {
  const response = await ApiClient.post("/assets/depreciation/post", data);
  return response.data;
}

export async function disposeAsset(data) {
  const response = await ApiClient.post("/assets/dispose", data);
  return response.data;
}

export async function addAssetDocument(data) {
  const response = await ApiClient.post("/assets/documents", data);
  return response.data;
}

export async function deleteAssetDocument(id) {
  const response = await ApiClient.delete(`/assets/documents/${id}`);
  return response.data;
}

export async function updateAssetSettings(data) {
  const response = await ApiClient.post("/assets/settings", data);
  return response.data;
}
